import styled from '@emotion/styled';
import { Tooltip, DropdownMenu, DropdownSelectTrigger } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { QuestionTypes } from '../../../../../domain/Surveys/Questions/QuestionTypes';
import { QUESTION_TYPES_SELECT_OPTIONS } from '../../../../../domain/Surveys/Questions/QuestionTypes';

const StyledSelectTrigger = styled(DropdownSelectTrigger)`
  max-width: 280px;

  svg:first-of-type {
    fill: rgba(0, 0, 0, 0.89);
  }
`;

export interface QuestionTypeSelectorProps {
  selectedType: QuestionTypes;
  isDisabled: boolean;
  onChange: (newType: QuestionTypes) => void;
}

export const QuestionTypeSelector = ({
  isDisabled,
  selectedType,
  onChange,
}: QuestionTypeSelectorProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.questions.question-type');
  const selectedOption = QUESTION_TYPES_SELECT_OPTIONS.find(({ value }) => value === selectedType);

  return (
    <Tooltip text={t('tooltip')} isDisabled={!isDisabled}>
      <DropdownMenu
        trigger={
          <StyledSelectTrigger
            aria-label={`${t('name')}${
              selectedOption ? `:${t(`label.${selectedOption?.label}`)}` : ''
            }`}
            size="m"
            icon={selectedOption?.icon}
            isDisabled={isDisabled}
            selectedOption={
              selectedOption && { ...selectedOption, label: t(`label.${selectedOption?.label}`) }
            }
            badge={selectedOption?.badge}
          />
        }
        appendTo="parent"
        onShow={({ popper, reference }) => {
          // Force to fit to the parent's width
          // see: https://storybook.hotjar.com/?path=/docs/components-dropdownselecttrigger
          (popper.firstChild as HTMLElement).style.width = `${
            reference.getBoundingClientRect().width
          }px`;
        }}
        isDisabled={isDisabled}
      >
        {QUESTION_TYPES_SELECT_OPTIONS.map((questionType) => (
          <DropdownMenu.Item
            key={questionType.value}
            isSelected={selectedType === questionType.value}
            icon={questionType.icon}
            onClick={() => onChange(questionType.value)}
            badge={questionType.badge}
          >
            {t(`label.${questionType.label}`)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>
    </Tooltip>
  );
};
